import React, { useState, useMemo } from 'react';
import {
    Modal,
    Button,
    DatePicker,
    Segmented,
    Alert,
    message,
    Typography
} from 'antd';
import {
    ReloadOutlined,
    CalendarOutlined,
    FieldTimeOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { Method_POST_CONTEXTO } from '../../../../service/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useDraggableModal } from "../Balancete/utils/modalDraggable";

dayjs.locale('pt-br');

const { Text } = Typography;
const { RangePicker } = DatePicker;

const ModalZerarPeriodo = ({ show, handleClose, onUpdate, zIndex, onFocus }) => {
    const draggableProps = useDraggableModal(zIndex, onFocus, {
        show,
        modalKey: 'zeramento',
        initialWidth: 520,
        initialHeight: 350,
        overflow: 'visible'
    });
    const [mode, setMode] = useState('year'); // year | custom
    const [dateValue, setDateValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const description = useMemo(() => {
        if (!dateValue) return null;

        if (mode === 'year') {
            return (
                <span>
                    Você está prestes a <b>ZERAR</b> o resultado contábil de
                    <b> {dateValue.format('YYYY')}</b>.
                    <br />
                    Essa ação irá gerar lançamentos automáticos de apuração
                    de <b>lucro ou prejuízo</b>.
                </span>
            );
        }

        if (mode === 'custom' && dateValue?.[0] && dateValue?.[1]) {
            return (
                <span>
                    Você está prestes a <b>ZERAR</b> o resultado contábil do período de
                    <b> {dateValue[0].format('MM/YYYY')}</b> até
                    <b> {dateValue[1].format('MM/YYYY')}</b>.
                    <br />
                    Essa ação irá gerar lançamentos automáticos de apuração
                    de <b>lucro ou prejuízo</b>.
                </span>
            );
        }

        return null;
    }, [dateValue, mode]);

    const handleZerar = async () => {
        if (!dateValue) {
            setErrorMsg('Selecione o período para zeramento.');
            return;
        }

        setLoading(true);
        setErrorMsg(null);

        try {
            let params = '';

            if (mode === 'year') {
                params = `?ano=${dateValue.year()}`;
            } else {
                params = `?dataInicio=${dateValue[0].startOf('month').format('YYYY-MM-DD')}` +
                    `&dataFim=${dateValue[1].endOf('month').format('YYYY-MM-DD')}`;
            }

            await Method_POST_CONTEXTO(
                `/empresa/contabilidade/zeramento/contabil${params}`
            );

            message.success('Zeramento realizado com sucesso!');
            if (onUpdate) onUpdate();
            handleCancel();

        } catch (err) {
            console.error(err);
            setErrorMsg('Erro ao realizar o zeramento do período.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setDateValue(null);
        setErrorMsg(null);
        handleClose();
    };

    return (
        <Modal
            open={show}
            onCancel={handleCancel}
            width={520}
            {...draggableProps}
            styles={{ body: { overflow: 'visible' } }}
            title={
                <span style={{ color: '#cf1322' }}>
                    <ReloadOutlined style={{ marginRight: 8 }} />
                    Zerar Período Contábil
                </span>
            }
            footer={[
                <Button key="cancel" onClick={handleCancel} disabled={loading}>
                    Cancelar
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    danger
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={handleZerar}
                    disabled={!dateValue}
                >
                    Zerar
                </Button>
            ]}
        >
            {errorMsg && (
                <Alert
                    type="error"
                    showIcon
                    message="Erro"
                    description={errorMsg}
                    style={{ marginBottom: 16 }}
                />
            )}

            <Alert
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined />}
                description={
                    description ||
                    "Selecione um período para zerar. Essa ação gera lançamentos contábeis automáticos e não pode ser desfeita."
                }
                style={{ marginBottom: 24 }}
            />

            <Text strong>Modo de zeramento:</Text>

            <Segmented
                block
                style={{ margin: '12px 0 16px' }}
                value={mode}
                onChange={(val) => {
                    setMode(val);
                    setDateValue(null);
                }}
                options={[
                    {
                        label: 'Ano Completo',
                        value: 'year',
                        icon: <CalendarOutlined />
                    },
                    {
                        label: 'Período Específico',
                        value: 'custom',
                        icon: <FieldTimeOutlined />
                    }
                ]}
            />

            {mode === 'year' ? (
                <DatePicker
                    picker="year"
                    style={{ width: '100%' }}
                    value={dateValue}
                    onChange={setDateValue}
                    format="YYYY"
                />
            ) : (
                <RangePicker
                    picker="month"
                    style={{ width: '100%' }}
                    value={dateValue}
                    onChange={setDateValue}
                    format="MM/YYYY"
                />
            )}
        </Modal>
    );
};

export default ModalZerarPeriodo;
